<script setup lang="ts">
import DSContainer from '~/components/ds/Container.vue'
import DSButtonLink from '~/components/ds/ButtonLink.vue'
import HeroBuyerScene from '~/components/hero/HeroBuyerScene.vue'
import HeroFinancingScene from '~/components/hero/HeroFinancingScene.vue'

const { t } = useI18n()
const localePath = useLocalePath()

type HeroSlide = {
  id: string
  gradient: string
  badgeIcon: string
  badge: string
  title: string
  subtitle: string
  description: string
  primaryCta: { text: string, link: string, icon: string }
  secondaryCta?: { text: string, link: string, icon: string }
  partner?: { label: string, name: string }
}

const slides = computed<HeroSlide[]>(() => [
  {
    id: 'buyer',
    gradient: 'from-brand-700 via-brand-800 to-brand-950',
    badgeIcon: 'i-lucide-badge-check',
    badge: t('hero.buyer.badge'),
    title: t('hero.buyer.title'),
    subtitle: t('hero.buyer.subtitle'),
    description: t('hero.buyer.description'),
    primaryCta: { text: t('hero.buyer.primaryCta'), link: '/shop', icon: 'i-lucide-shopping-bag' },
    secondaryCta: { text: t('hero.buyer.secondaryCta'), link: '/compramos', icon: 'i-lucide-hand-coins' },
  },
  {
    id: 'financing',
    gradient: 'from-indigo-800 via-violet-900 to-purple-950',
    badgeIcon: 'i-lucide-credit-card',
    badge: t('hero.financing.badge'),
    title: t('hero.financing.title'),
    subtitle: t('financing.noFees'),
    description: t('hero.financing.description'),
    primaryCta: { text: t('hero.financing.primaryCta'), link: '/shop', icon: 'i-lucide-shopping-bag' },
    partner: { label: t('hero.financing.poweredBy'), name: 'Klarna.' },
  },
])

const currentIndex = ref(0)
// The first slide is SSR content and must be paintable immediately. Animating it
// from opacity: 0 makes the hero copy become the LCP element only after hydration.
// Keep the transition for deliberate/autoplay slide changes instead.
const hasChangedSlide = ref(false)
const isPointerPaused = ref(false)
const isUserPaused = ref(false)
const prefersReducedMotion = ref(false)
const isHeroVisible = ref(true)
const isDocumentVisible = ref(true)
const heroRef = ref<HTMLElement | null>(null)
let autoplayTimer: ReturnType<typeof setInterval> | null = null
let reducedMotionQuery: MediaQueryList | null = null
let reducedMotionHandler: ((event: MediaQueryListEvent) => void) | null = null
let visibilityObserver: IntersectionObserver | null = null
let touchStartX = 0
let touchStartY = 0
let touchIdentifier: number | null = null
let touchStartedOnInteractive = false

function goTo(index: number) {
  hasChangedSlide.value = true
  currentIndex.value = (index + slides.value.length) % slides.value.length
}

function stopAutoplay() {
  if (!autoplayTimer) return
  clearInterval(autoplayTimer)
  autoplayTimer = null
}

function startAutoplay() {
  stopAutoplay()
  if (
    prefersReducedMotion.value
    || isUserPaused.value
    || !isHeroVisible.value
    || !isDocumentVisible.value
    || slides.value.length < 2
  ) return
  autoplayTimer = setInterval(() => {
    if (!isPointerPaused.value) goTo(currentIndex.value + 1)
  }, 7500)
}

function toggleAutoplay() {
  isUserPaused.value = !isUserPaused.value
  startAutoplay()
}

async function navigateManually(index: number, restoreMobileFocus = false) {
  goTo(index)
  startAutoplay()
  if (!restoreMobileFocus) return

  await nextTick()
  const nextControl = heroRef.value?.querySelector<HTMLButtonElement>(
    `article[aria-hidden="false"] [data-hero-mobile-dot="${currentIndex.value}"]`,
  )
  nextControl?.focus({ preventScroll: true })
}

function handleVisibilityChange() {
  isDocumentVisible.value = document.visibilityState === 'visible'
  startAutoplay()
}

function handleTouchStart(event: TouchEvent) {
  touchStartedOnInteractive = event.target instanceof Element
    && Boolean(event.target.closest('a, button, input, select, textarea'))
  if (touchStartedOnInteractive || event.touches.length > 1) {
    touchIdentifier = null
    return
  }

  const touch = event.changedTouches[0]
  if (!touch) return
  touchIdentifier = touch.identifier
  touchStartX = touch.clientX
  touchStartY = touch.clientY
}

function handleTouchEnd(event: TouchEvent) {
  if (touchStartedOnInteractive || touchIdentifier === null) return
  const touch = Array.from(event.changedTouches).find(item => item.identifier === touchIdentifier)
  touchIdentifier = null
  if (!touch) return

  const deltaX = touch.clientX - touchStartX
  const deltaY = touch.clientY - touchStartY
  if (Math.abs(deltaX) < 48 || Math.abs(deltaX) < Math.abs(deltaY) * 1.2) return

  goTo(currentIndex.value + (deltaX < 0 ? 1 : -1))
  startAutoplay()
}

function handleTouchCancel() {
  touchIdentifier = null
  touchStartedOnInteractive = false
}

onMounted(() => {
  reducedMotionQuery = window.matchMedia('(prefers-reduced-motion: reduce)')
  prefersReducedMotion.value = reducedMotionQuery.matches
  reducedMotionHandler = (event) => {
    prefersReducedMotion.value = event.matches
    startAutoplay()
  }
  if (reducedMotionQuery.addEventListener) {
    reducedMotionQuery.addEventListener('change', reducedMotionHandler)
  }
  else {
    reducedMotionQuery.addListener(reducedMotionHandler)
  }

  isDocumentVisible.value = document.visibilityState === 'visible'
  document.addEventListener('visibilitychange', handleVisibilityChange)

  if ('IntersectionObserver' in window) {
    visibilityObserver = new IntersectionObserver(([entry]) => {
      isHeroVisible.value = Boolean(entry?.isIntersecting && entry.intersectionRatio >= 0.5)
      startAutoplay()
    }, { threshold: [0, 0.5] })
    if (heroRef.value) visibilityObserver.observe(heroRef.value)
  }

  startAutoplay()
})

onBeforeUnmount(() => {
  stopAutoplay()
  document.removeEventListener('visibilitychange', handleVisibilityChange)
  visibilityObserver?.disconnect()
  if (reducedMotionQuery && reducedMotionHandler) {
    if (reducedMotionQuery.removeEventListener) {
      reducedMotionQuery.removeEventListener('change', reducedMotionHandler)
    }
    else {
      reducedMotionQuery.removeListener(reducedMotionHandler)
    }
  }
})
</script>

<template>
  <section
    ref="heroRef"
    class="relative mx-4 my-4 touch-pan-y overflow-hidden rounded-2xl text-white sm:mx-8 lg:mx-auto lg:max-w-7xl"
    aria-roledescription="carousel"
    :aria-label="t('hero.carouselLabel')"
    @mouseenter="isPointerPaused = true"
    @mouseleave="isPointerPaused = false"
    @focusin="isPointerPaused = true"
    @focusout="isPointerPaused = false"
    @touchstart.passive="handleTouchStart"
    @touchend.passive="handleTouchEnd"
    @touchcancel.passive="handleTouchCancel"
  >
    <h1 class="sr-only">
      {{ t('hero.title') }} — {{ t('hero.subtitle') }}
    </h1>

    <div
      class="flex"
      :class="prefersReducedMotion ? '' : 'transition-transform duration-700 ease-out'"
      :style="{ transform: `translateX(-${currentIndex * 100}%)` }"
    >
      <article
        v-for="(slide, index) in slides"
        :key="slide.id"
        class="relative w-full shrink-0 bg-gradient-to-br"
        :class="[
          slide.gradient,
          {
            'hero-slide--active': currentIndex === index,
            'hero-slide--animate-copy': currentIndex === index && hasChangedSlide,
          },
        ]"
        aria-roledescription="slide"
        :aria-label="`${index + 1} / ${slides.length}`"
        :aria-hidden="currentIndex !== index"
        :inert="currentIndex !== index"
      >
        <!-- Reserve dedicated side gutters for carousel arrows so controls
             never overlap the slide copy or illustration. -->
        <DSContainer class="relative pb-20 pt-10 sm:px-20 sm:pb-20 sm:pt-14 lg:px-24 lg:pb-24 lg:pt-16 xl:px-28 xl:py-16">
          <div class="pointer-events-none absolute inset-0 opacity-10" aria-hidden="true">
            <div class="absolute inset-0 bg-[radial-gradient(circle_at_2px_2px,white_1px,transparent_0)] bg-[length:40px_40px]" />
          </div>

          <div class="relative grid items-center gap-10 xl:grid-cols-[minmax(0,3fr)_minmax(20rem,2fr)]">
            <div class="min-w-0 max-w-3xl">
              <div class="hero-copy-step mb-4 inline-flex items-center gap-2 rounded-full bg-white/10 px-3 py-1.5 text-sm font-semibold ring-1 ring-white/20 [--hero-delay:100ms]">
                <UIcon :name="slide.badgeIcon" class="size-4 text-emerald-200" />
                {{ slide.badge }}
              </div>

              <h2
                class="hero-copy-step max-w-3xl text-3xl font-bold leading-tight sm:text-4xl lg:text-5xl [--hero-delay:80ms]"
              >
                {{ slide.title }}
                <span class="block text-emerald-200">{{ slide.subtitle }}</span>
              </h2>
              <p class="hero-copy-step mt-5 max-w-2xl text-base leading-relaxed text-white/85 sm:text-lg [--hero-delay:130ms]">
                {{ slide.description }}
              </p>
              <div v-if="slide.partner" class="hero-copy-step mt-3 flex items-center gap-2 text-sm text-white/80 [--hero-delay:160ms]">
                <span>{{ slide.partner.label }}</span>
                <span class="rounded-md bg-[#FFA8CD] px-2 py-0.5 font-bold text-[#17120F]">
                  {{ slide.partner.name }}
                </span>
              </div>

              <div class="mt-5 flex items-end justify-between gap-4 xl:hidden">
                <div class="h-24 w-32 shrink-0 sm:ml-auto sm:h-28 sm:w-36 lg:h-40 lg:w-52">
                  <HeroBuyerScene
                    v-if="slide.id === 'buyer'"
                    :active="currentIndex === index"
                    :reduced-motion="prefersReducedMotion"
                    compact
                  />
                  <HeroFinancingScene
                    v-else
                    :active="currentIndex === index"
                    :reduced-motion="prefersReducedMotion"
                    compact
                  />
                </div>

                <div class="flex items-center rounded-full bg-black/20 p-1 backdrop-blur-sm sm:hidden">
                  <button
                    v-for="(controlSlide, controlIndex) in slides"
                    :key="controlSlide.id"
                    type="button"
                    :aria-label="t('hero.goToSlide', { n: controlIndex + 1 })"
                    :aria-current="currentIndex === controlIndex ? 'true' : undefined"
                    :data-hero-mobile-dot="controlIndex"
                    class="group flex size-11 items-center justify-center rounded-full focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-white"
                    @click="navigateManually(controlIndex, true)"
                  >
                    <span
                      class="h-2 rounded-full transition-[width,background-color]"
                      :class="currentIndex === controlIndex ? 'w-8 bg-white' : 'w-2 bg-white/50 group-hover:bg-white/80'"
                    />
                  </button>
                  <button
                    v-if="!prefersReducedMotion"
                    type="button"
                    :aria-label="isUserPaused ? t('hero.play') : t('hero.pause')"
                    :aria-pressed="isUserPaused"
                    class="flex size-11 items-center justify-center rounded-full text-white transition hover:bg-white/15 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-white"
                    @click="toggleAutoplay"
                  >
                    <UIcon :name="isUserPaused ? 'i-lucide-play' : 'i-lucide-pause'" class="size-4" />
                  </button>
                </div>
              </div>

              <div class="hero-actions hero-copy-step mt-6 flex min-w-0 flex-col gap-3 [--hero-delay:180ms] sm:mt-8 sm:flex-row">
                <DSButtonLink
                  :to="localePath(slide.primaryCta.link)"
                  size="lg"
                  variant="inverted"
                  :icon="slide.primaryCta.icon"
                  :tabindex="currentIndex === index ? 0 : -1"
                  class="hero-cta"
                >
                  {{ slide.primaryCta.text }}
                </DSButtonLink>
                <DSButtonLink
                  v-if="slide.secondaryCta"
                  :to="localePath(slide.secondaryCta.link)"
                  size="lg"
                  variant="outline-inverted"
                  :icon="slide.secondaryCta.icon"
                  :tabindex="currentIndex === index ? 0 : -1"
                  class="hero-cta"
                >
                  {{ slide.secondaryCta.text }}
                </DSButtonLink>
              </div>
            </div>

            <div class="hidden xl:block" aria-hidden="true">
              <div class="relative mx-auto aspect-square max-w-sm">
                <HeroBuyerScene
                  v-if="slide.id === 'buyer'"
                  :active="currentIndex === index"
                  :reduced-motion="prefersReducedMotion"
                />
                <HeroFinancingScene
                  v-else
                  :active="currentIndex === index"
                  :reduced-motion="prefersReducedMotion"
                />
              </div>
            </div>
          </div>
        </DSContainer>
      </article>
    </div>

    <template v-if="slides.length > 1">
      <button
        type="button"
        :aria-label="t('hero.prev')"
        class="absolute left-3 top-1/2 z-20 hidden size-11 -translate-y-1/2 items-center justify-center rounded-full bg-black/25 text-white backdrop-blur-sm transition hover:bg-black/45 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-white sm:flex"
        @click="navigateManually(currentIndex - 1)"
      >
        <UIcon name="i-lucide-chevron-left" class="size-6" />
      </button>
      <button
        type="button"
        :aria-label="t('hero.next')"
        class="absolute right-3 top-1/2 z-20 hidden size-11 -translate-y-1/2 items-center justify-center rounded-full bg-black/25 text-white backdrop-blur-sm transition hover:bg-black/45 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-white sm:flex"
        @click="navigateManually(currentIndex + 1)"
      >
        <UIcon name="i-lucide-chevron-right" class="size-6" />
      </button>

      <div class="absolute bottom-4 left-1/2 z-20 hidden -translate-x-1/2 items-center rounded-full bg-black/20 px-1 py-1 backdrop-blur-sm sm:flex">
        <button
          v-for="(slide, index) in slides"
          :key="slide.id"
          type="button"
          :aria-label="t('hero.goToSlide', { n: index + 1 })"
          :aria-current="currentIndex === index ? 'true' : undefined"
          class="group flex size-11 items-center justify-center rounded-full focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-white"
          @click="navigateManually(index)"
        >
          <span
            class="h-2 rounded-full transition-[width,background-color]"
            :class="currentIndex === index ? 'w-8 bg-white' : 'w-2 bg-white/50 group-hover:bg-white/80'"
          />
        </button>
        <button
          v-if="!prefersReducedMotion"
          type="button"
          :aria-label="isUserPaused ? t('hero.play') : t('hero.pause')"
          :aria-pressed="isUserPaused"
          class="flex size-11 items-center justify-center rounded-full text-white transition hover:bg-white/15 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-white"
          @click="toggleAutoplay"
        >
          <UIcon :name="isUserPaused ? 'i-lucide-play' : 'i-lucide-pause'" class="size-4" />
        </button>
      </div>
    </template>
  </section>
</template>

<style scoped>
.hero-slide--animate-copy .hero-copy-step {
  animation: hero-copy-in 420ms cubic-bezier(.22, 1, .36, 1) var(--hero-delay, 0ms) both;
}

@keyframes hero-copy-in {
  from {
    opacity: 0;
    transform: translateY(16px);
  }

  to {
    opacity: 1;
    transform: translateY(0);
  }
}

@media (prefers-reduced-motion: reduce) {
  .hero-slide--animate-copy .hero-copy-step {
    animation: none;
  }
}

@media (max-width: 639px) {
  .hero-actions :deep(.hero-cta) {
    width: 100%;
    max-width: 100%;
    min-width: 0;
    padding-inline: 12px;
    text-align: center;
    white-space: normal;
  }

  .hero-actions :deep(.hero-cta .iconify) {
    width: 20px;
    height: 20px;
    flex: 0 0 20px;
  }
}
</style>
